import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash2, Info } from 'lucide-react';
import type { PriceOption } from '../../types/entities';

interface PriceOptionsEditorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
}

export function PriceOptionsEditor({
  value,
  onChange,
  label = 'Options de tarification',
}: PriceOptionsEditorProps) {
  const [options, setOptions] = useState<PriceOption[]>([]);

  // Parse JSON value on mount and when value changes
  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          setOptions(parsed);
        }
      } catch {
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  }, [value]);

  // Serialize options to JSON and notify parent
  const updateParent = (newOptions: PriceOption[]) => {
    setOptions(newOptions);
    if (newOptions.length === 0) {
      onChange(null);
    } else {
      onChange(JSON.stringify(newOptions));
    }
  };

  const addOption = () => {
    const newOptions = [...options, { duration: 60, price: 60 }];
    updateParent(newOptions);
  };

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    updateParent(newOptions);
  };

  const updateOption = (index: number, field: keyof PriceOption, val: string | number) => {
    const newOptions = options.map((opt, i) => {
      if (i === index) {
        if (field === 'label') {
          // If label is empty string, remove it
          if (val === '') {
            const { label: _, ...rest } = opt;
            return rest;
          }
          return { ...opt, label: val as string };
        }
        return { ...opt, [field]: val };
      }
      return opt;
    });
    updateParent(newOptions);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addOption}
          className="gap-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Ajouter une option
        </Button>
      </div>

      {options.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Aucune option de tarification</p>
            <p className="text-blue-700">
              Si vous ajoutez des options de tarification multiples (ex: 30min - 40€, 60min - 70€),
              elles seront affichées à la place du prix et de la durée uniques.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {options.map((option, index) => (
            <div
              key={index}
              className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border"
            >
              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-gray-600 mb-1 block">
                  Durée (min)
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={option.duration || ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v) && v > 0) updateOption(index, 'duration', v);
                  }}
                  className="h-9"
                />
              </div>

              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-gray-600 mb-1 block">
                  Prix (€)
                </Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={option.price || ''}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v > 0) updateOption(index, 'price', v);
                  }}
                  className="h-9"
                />
              </div>

              <div className="flex-[2] min-w-[150px]">
                <Label className="text-xs text-gray-600 mb-1 block">
                  Label (optionnel)
                </Label>
                <Input
                  type="text"
                  placeholder="Ex: Séance découverte"
                  value={option.label || ''}
                  onChange={(e) => updateOption(index, 'label', e.target.value)}
                  className="h-9"
                />
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeOption(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 px-2"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <p className="text-xs text-gray-500 mt-2">
            Les options de tarification remplacent le prix et la durée uniques lors de l'affichage.
          </p>
        </div>
      )}
    </div>
  );
}
